<template>
    <div class="sale-simple-view">
        <div class="page-header">
            <div class="flex">
                <GoBackTitle title="Ventes comptant" :to="{ name: 'cash-registers' }" />
            </div>
            <BaseButton @click="openPaymentModal" class="validate-btn" :disabled="cart.length === 0 || isProcessing">
                <span v-if="isProcessing" class="spinner-small"></span>
                <span v-else>Valider la vente</span>
            </BaseButton>
        </div>

        <div class="sale-container">
            <div class="products-column">
                <div class="search-container">
                    <div class="search-input-wrapper">
                        <i class="fas fa-search" style="width: 18px; height: 18px;"></i>
                        <input ref="searchInput" type="text" v-model="searchQuery"
                            placeholder="Rechercher un produit (Nom, Code-barres, Réf)..." @input="searchProducts"
                            @keydown.down.prevent="navigateProducts('down')"
                            @keydown.up.prevent="navigateProducts('up')"
                            @keydown.enter.prevent="selectHighlightedProduct" class="search-input" />
                        <BaseButton v-if="searchQuery" @click="clearSearch" class="clear-search-btn">
                            <i class="fas fa-times" style="width: 16px; height: 16px;"></i>
                        </BaseButton>
                    </div>

                    <div class="categories-filter">
                        <select v-model="selectedCategory" @change="filterByCategory">
                            <option value="">Toutes les catégories</option>
                            <option v-for="category in categories" :key="category.id" :value="category.id">
                                {{ category.name }}
                            </option>
                        </select>
                    </div>
                </div>

                <div class="products-list">
                    <div v-if="loading" class="loading-container">
                        <div class="spinner"></div>
                        <p>Chargement des produits...</p>
                    </div>

                    <div v-else-if="filteredProducts.length === 0" class="empty-results">
                        <i class="fas fa-box-open" style="width: 48px; height: 48px; font-size: 48px;"></i>
                        <p>Aucun produit trouvé</p>
                        <BaseButton v-if="searchQuery" @click="clearSearch" class="clear-search-link">
                            Effacer la recherche
                        </BaseButton>
                    </div>

                    <div v-else class="products-table-container">
                        <table class="products-table">
                            <thead>
                                <tr>
                                    <th class="col-image">Image</th>
                                    <th class="col-name">Produit</th>
                                    <th class="col-category">Catégorie</th>
                                    <th class="col-stock">Stock</th>
                                    <th class="col-price">Prix</th>
                                    <th class="col-action"></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(product, index) in filteredProducts" :key="product.id" class="product-row"
                                    :class="{ 'highlighted': highlightedProductIndex === index, 'product-disabled': isProductDisabled(product) }"
                                    @click="addToCart(product)" @mouseenter="highlightedProductIndex = index">
                                    <td class="col-image">
                                        <div class="product-image-small">
                                            <img v-if="product.image_url" :src="product.image_url"
                                                :alt="product.name" />
                                            <div v-else class="placeholder-image-small">
                                                <i class="fas fa-image" style="width: 16px; height: 16px;"></i>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="col-name">
                                        <span class="product-name-text">{{ product.name }}</span>
                                        <span v-if="product.reference" class="product-reference">Réf: {{
                                            product.reference }}</span>
                                    </td>
                                    <td class="col-category">
                                        <BaseBadge class="category-badge">{{ product.category?.name || '-' }}</BaseBadge>
                                    </td>
                                    <td class="col-stock">
                                        <BaseBadge class="stock-badge" :class="getStockClass(product.stock_quantity)">
                                            {{ product.stock_quantity || 0 }}
                                        </BaseBadge>
                                    </td>
                                    <td class="col-price">
                                        <span class="product-price-text">{{ formatCurrency(product.price_incl_tax)
                                            }}</span>
                                    </td>
                                    <td class="col-action" @click.stop>
                                        <BaseButton class="add-btn" :disabled="isProductDisabled(product)" @click="addToCart(product)">
                                            <i class="fas fa-plus" style="width: 14px; height: 14px;"></i>
                                        </BaseButton>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div v-if="cart.length > 0" class="quick-actions-bar">
                    <div class="quick-info">
                        <span class="items-count">{{ cart.length }} article(s)</span>
                        <span class="quick-total">Total: {{ formatCurrency(total) }}</span>
                    </div>
                    <BaseButton @click="openPaymentModal" class="quick-validate-btn">
                        Finaliser
                        <i class="fas fa-chevron-right" style="width: 16px; height: 16px;"></i>
                    </BaseButton>
                </div>
            </div>

            <div class="cart-column">
                <div class="cart-header">
                    <h2>Panier ({{ cart.length }})</h2>
                    <BaseButton v-if="cart.length > 0" @click="clearCart" class="clear-cart-btn">
                        <i class="fas fa-trash" style="width: 16px; height: 16px;"></i>
                        Vider
                    </BaseButton>
                </div>

                <div v-if="cart.length === 0" class="empty-cart">
                    <div class="empty-cart-icon">
                        <i class="fas fa-shopping-cart" style="font-size: 28px;"></i>
                    </div>
                    <p>Votre panier est vide</p>
                    <p class="empty-cart-hint">Cliquez sur un produit ou utilisez la recherche</p>
                </div>

                <div v-else class="cart-items">
                    <div v-for="(item, index) in cart" :key="index" class="cart-item">
                        <div class="item-details">
                            <div class="item-name">{{ item.name }}</div>
                            <div class="item-price">
                                <input type="number" class="price-input" v-model.number="item.price_incl_tax" min="0"
                                    step="1" @input="onUnitPriceInput(index)" @focus="$event.target.select()" />
                                × {{ item.quantity }}
                            </div>
                        </div>
                        <div class="item-actions">
                            <div class="item-quantity">
                                <button type="button" @click="decrementQuantity(index)" class="quantity-btn">
                                    <i class="fas fa-minus" style="width: 14px; height: 14px;"></i>
                                </button>
                                <input type="number" v-model.number="item.quantity" min="1"
                                    @change="updateItemTotal(index)" @focus="$event.target.select()">
                                <button type="button" @click="incrementQuantity(index)" class="quantity-btn">
                                    <i class="fas fa-plus" style="width: 14px; height: 14px;"></i>
                                </button>
                            </div>
                            <div class="item-total">{{ formatCurrency(item.total) }}</div>
                            <button type="button" @click="removeFromCart(index)" class="remove-item-btn">
                                <i class="fas fa-times" style="width: 16px; height: 16px;"></i>
                            </button>
                        </div>
                    </div>
                </div>

                <div v-if="cart.length > 0" class="cart-summary">
                    <div class="summary-row subtotal-row">
                        <span>Sous-total</span>
                        <span class="subtotal-amount">{{ formatCurrency(subtotal) }}</span>
                    </div>

                    <div class="summary-row discount-row">
                        <label class="discount-label">
                            <i class="fas fa-percentage" style="width: 16px; height: 16px;"></i>
                            Remise
                        </label>
                        <div class="discount-input">
                            <input type="number" v-model.number="discount" min="0" :max="subtotal" placeholder="0"
                                @input="calculateTotal" @focus="$event.target.select()">
                            <span class="currency-symbol">FCFA</span>
                        </div>
                    </div>

                    <div class="summary-row total-row">
                        <span>Total à payer</span>
                        <span class="total-amount">{{ formatCurrency(total) }}</span>
                    </div>

                    <BaseButton @click="openPaymentModal" class="validate-cart-btn"
                        :disabled="cart.length === 0 || isProcessing">
                        <span v-if="isProcessing" class="spinner-small"></span>
                        <template v-else>
                            <i class="fa-solid fa-check"></i>
                            Valider la vente
                        </template>
                    </BaseButton>
                </div>
            </div>
        </div>

        <!-- Modal choix mode de paiement -->
        <div v-if="showPaymentModal" class="modal-overlay" @click.self="closePaymentModal">
            <div class="modal-container payment-modal-container">
                <div class="modal-body">
                    <h2 class="payment-modal-title">Finaliser la vente</h2>
                    <div class="payment-modal-total">
                        <span>Total à payer</span>
                        <span class="total-amount">{{ formatCurrency(total) }}</span>
                    </div>
                    <p class="payment-subtitle">Choisir le mode de paiement</p>
                    <div class="payment-options">
                        <div v-for="method in paymentMethods" :key="method.value"
                            @click="selectedPaymentMethod = method.value" class="payment-option"
                            :class="{ 'active': selectedPaymentMethod === method.value }" tabindex="0"
                            @keydown.enter="selectedPaymentMethod = method.value">
                            <div class="payment-icon" v-html="method.icon"></div>
                            <span class="payment-name">{{ method.label }}</span>
                            <div v-if="selectedPaymentMethod === method.value" class="payment-check">
                                <i class="fas fa-check" style="width: 12px; height: 12px;"></i>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="modal-footer">
                    <BaseButton @click="closePaymentModal" class="back-to-sales-btn">Annuler</BaseButton>
                    <BaseButton @click="confirmPayment" class="new-sale-btn" :disabled="!selectedPaymentMethod || isProcessing">
                        <span v-if="isProcessing" class="spinner-small"></span>
                        <template v-else>
                            <i class="fa-solid fa-check"></i>
                            Confirmer le paiement
                        </template>
                    </BaseButton>
                </div>
            </div>
        </div>

        <div v-if="showSuccessModal" class="modal-overlay" @click.self="goBack">
            <div class="modal-container success-modal">
                <div class="modal-body text-center">
                    <div class="success-icon">
                        <i class="fas fa-check-circle" style="width: 64px; height: 64px; font-size: 64px;"></i>
                    </div>
                    <h2>Vente réalisée avec succès !</h2>
                    <p>Facture N° <span class="invoice-number">{{ completedSale.invoice_number }}</span></p>
                    <div class="success-details">
                        <div class="detail-row">
                            <span>Articles</span>
                            <span class="detail-value">{{ completedSale.items?.length || cart.length }}</span>
                        </div>
                        <div class="detail-row">
                            <span>Mode de paiement</span>
                            <span class="detail-value">{{ getPaymentMethodLabel(selectedPaymentMethod) }}</span>
                        </div>
                    </div>
                    <div class="success-total">
                        <span>Total</span>
                        <span class="success-amount">{{ formatCurrency(completedSale.total_incl_tax) }}</span>
                    </div>
                </div>

                <div class="modal-footer success-modal-footer">
                    <div class="action-cards">
                        <BaseButton @click="printReceipt(completedSale.id)" class="action-card">
                            <div class="action-card-icon invoice-icon">
                                <i class="fas fa-file-invoice" style="width: 28px; height: 28px; font-size: 28px;"></i>
                            </div>
                            <span class="action-card-label">Facture</span>
                        </BaseButton>

                        <BaseButton @click="printTicket(completedSale)" class="action-card">
                            <div class="action-card-icon ticket-icon">
                                <i class="fas fa-receipt" style="width: 28px; height: 28px; font-size: 28px;"></i>
                            </div>
                            <span class="action-card-label">Ticket</span>
                        </BaseButton>

                        <BaseButton @click="newSale" class="action-card primary">
                            <div class="action-card-icon new-sale-icon">
                                <i class="fas fa-plus" style="width: 28px; height: 28px; font-size: 28px;"></i>
                            </div>
                            <span class="action-card-label">Nouvelle vente</span>
                        </BaseButton>
                    </div>

                    <BaseButton @click="goBack" class="back-link">
                        <i class="fas fa-arrow-left" style="width: 16px; height: 16px;"></i>
                        Retour aux ventes
                    </BaseButton>
                </div>
            </div>
        </div>

        <!-- Modal Informations Client pour Facture -->
        <div v-if="showInvoiceModal" class="modal-overlay" @click.self="closeInvoiceModal">
            <div class="modal invoice-modal">
                <div class="modal-header">
                    <h2>Informations pour la facture</h2>
                    <BaseButton @click="closeInvoiceModal" class="close-btn">
                        <i class="fas fa-times" style="width: 20px; height: 20px;"></i>
                    </BaseButton>
                </div>
                <div class="modal-body">
                    <p class="modal-description">Veuillez renseigner les informations du client pour générer la facture.</p>
                    
                    <div class="form-group">
                        <label for="clientName">Nom du client <span class="required">*</span></label>
                        <input 
                            type="text" 
                            id="clientName" 
                            v-model="invoiceClientName" 
                            placeholder="Entrez le nom du client"
                            class="form-input"
                            :class="{ 'input-error': invoiceErrors.name }"
                        />
                        <span v-if="invoiceErrors.name" class="error-message">{{ invoiceErrors.name }}</span>
                    </div>
                    
                    <div class="form-group">
                        <label for="clientPhone">Téléphone <span class="required">*</span></label>
                        <input 
                            type="tel" 
                            id="clientPhone" 
                            v-model="invoiceClientPhone" 
                            placeholder="Ex: 77 123 45 67"
                            class="form-input"
                            :class="{ 'input-error': invoiceErrors.phone }"
                        />
                        <span v-if="invoiceErrors.phone" class="error-message">{{ invoiceErrors.phone }}</span>
                    </div>
                </div>
                <div class="modal-footer invoice-modal-footer">
                    <BaseButton @click="closeInvoiceModal" class="btn-cancel">Annuler</BaseButton>
                    <BaseButton @click="confirmDownloadInvoice" class="btn-confirm" :disabled="isDownloadingInvoice">
                        <span v-if="isDownloadingInvoice" class="spinner-small"></span>
                        <span v-else>Télécharger la facture</span>
                    </BaseButton>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { useRouter } from 'vue-router'
import { productsService, salesService, categoriesService } from '@/services'
import CashRegisterService from '@/services/cashRegisterService'
import { useToastStore } from '@/store/modules/toastStore'
import thermalPrinterService from '@/services/thermalPrinterService'
import GoBackTitle from '@/components/base/GoBackTitle.vue'
import BaseBadge from '@/components/base/BaseBadge.vue'
import { defineComponent, ref, computed, onMounted, onUnmounted, nextTick } from 'vue'

export default defineComponent({
    name: 'SaleSimple',
    components: {
        GoBackTitle,
        BaseBadge
    },
    setup() {
        const router = useRouter()
        const toastStore = useToastStore()

        const loading = ref(true)
        const products = ref([])
        const categories = ref([])
        const searchQuery = ref('')
        const selectedCategory = ref('')
        const cart = ref([])
        const discount = ref(0)
        const isProcessing = ref(false)
        const showSuccessModal = ref(false)
        const showPaymentModal = ref(false)
        const completedSale = ref({})
        const selectedPaymentMethod = ref('cash')
        const activeCashRegister = ref(null)
        const highlightedProductIndex = ref(-1)
        const searchInput = ref(null)
        const paymentSection = ref(null)
        const searchTimeout = ref(null)

        // Company settings from localStorage
        const companySettings = ref(null)

        const loadCompanySettings = () => {
            try {
                const settings = localStorage.getItem('company_settings')
                if (settings) {
                    companySettings.value = JSON.parse(settings)
                }
            } catch (error) {
                console.error('Error loading company settings:', error)
            }
        }

        const isProductDisabled = (product) => {
            if (!companySettings.value) return false
            const allowZeroQuantity = companySettings.value.allow_zero_quantity_sales
            if (allowZeroQuantity === false || allowZeroQuantity === 0) {
                const stockQty = product.stock_quantity || 0
                return stockQty <= 0
            }
            return false
        }
        
        // Invoice modal
        const showInvoiceModal = ref(false)
        const invoiceClientName = ref('')
        const invoiceClientPhone = ref('')
        const invoiceErrors = ref({ name: '', phone: '' })
        const isDownloadingInvoice = ref(false)
        const invoiceSaleId = ref(null)

        const handleKeyboard = (event) => {
            // Touche Escape - fermer les modals
            if (event.key === 'Escape') {
                if (showInvoiceModal.value) {
                    closeInvoiceModal()
                } else if (showSuccessModal.value) {
                    goBack()
                } else if (showPaymentModal.value) {
                    closePaymentModal()
                }
            }
            
            // Ctrl+Enter ou Ctrl+Space - Ouvrir modal paiement / Valider vente rapidement
            if ((event.ctrlKey || event.metaKey) && (event.key === 'Enter' || event.key === ' ')) {
                event.preventDefault()
                
                // Si aucun modal ouvert et panier non vide -> ouvrir modal paiement
                if (!showPaymentModal.value && !showSuccessModal.value && !showInvoiceModal.value && cart.value.length > 0) {
                    openPaymentModal()
                }
                // Si modal paiement ouvert et méthode sélectionnée -> confirmer
                else if (showPaymentModal.value && selectedPaymentMethod.value && !isProcessing.value) {
                    confirmPayment()
                }
                return
            }
            
            // Touche Enter seule - confirmer les actions dans les modals
            if (event.key === 'Enter' && !event.ctrlKey && !event.metaKey) {
                // Confirmer le paiement si modal de paiement ouvert et méthode sélectionnée
                if (showPaymentModal.value && selectedPaymentMethod.value && !isProcessing.value) {
                    event.preventDefault()
                    confirmPayment()
                }
                // Confirmer téléchargement facture si modal ouvert
                else if (showInvoiceModal.value && !isDownloadingInvoice.value) {
                    event.preventDefault()
                    confirmDownloadInvoice()
                }
            }
        }

        onMounted(async () => {
            document.addEventListener('keydown', handleKeyboard)
            loadCompanySettings()
            await Promise.all([loadProducts(), loadCategories(), loadActiveCashRegister()])
            nextTick(() => {
                searchInput.value?.focus()
            })
        })

        onUnmounted(() => {
            document.removeEventListener('keydown', handleKeyboard)
        })

        const paymentMethods = [
            {
                value: 'cash',
                label: 'Espèces',
                icon: '<i class="fas fa-dollar-sign" style="width: 24px; height: 24px; font-size: 24px;"></i>'
            },
            {
                value: 'mobile_money',
                label: 'Mobile Money',
                icon: '<i class="fas fa-mobile-alt" style="width: 24px; height: 24px; font-size: 24px;"></i>'
            },
            {
                value: 'card',
                label: 'Carte',
                icon: '<i class="fas fa-credit-card" style="width: 24px; height: 24px; font-size: 24px;"></i>'
            },
            {
                value: 'bank_transfer',
                label: 'Virement',
                icon: '<i class="fas fa-exchange-alt" style="width: 24px; height: 24px; font-size: 24px;"></i>'
            },
            {
                value: 'check',
                label: 'Chèque',
                icon: '<i class="fas fa-money-check" style="width: 24px; height: 24px; font-size: 24px;"></i>'
            }
        ]

        const filteredProducts = computed(() => {
            let result = [...products.value]
            if (selectedCategory.value) {
                result = result.filter(product => product.category_id === selectedCategory.value)
            }
            return result
        })

        const subtotal = computed(() => {
            return cart.value.reduce((sum, item) => sum + item.total, 0)
        })

        const total = computed(() => {
            return Math.max(0, subtotal.value - (discount.value || 0))
        })

        const loadProducts = async () => {
            try {
                loading.value = true
                const response = await productsService.getProducts({
                    per_page: 10,
                    with: ['category'],
                    search: searchQuery.value
                })
                products.value = response.data.data || response.data || []
                highlightedProductIndex.value = products.value.length > 0 ? 0 : -1
            } catch (error) {
                toastStore.addToast({
                    type: 'error',
                    message: 'Erreur lors du chargement des produits',
                    duration: 4000
                })
            } finally {
                loading.value = false
            }
        }

        const loadCategories = async () => {
            try {
                const response = await categoriesService.getCategories()
                categories.value = response.data.data || response.data || []
            } catch (error) {
                console.error('Erreur lors du chargement des catégories:', error)
            }
        }

        const loadActiveCashRegister = async () => {
            try {
                const response = await CashRegisterService.getCashRegisters({ status: 'open' })
                const openRegisters = response.data.data || response.data || []
                if (openRegisters.length > 0) {
                    activeCashRegister.value = openRegisters[0]
                } else {
                    toastStore.addToast({
                        type: 'error',
                        message: 'Aucune caisse ouverte. Veuillez ouvrir une caisse avant de faire une vente.',
                        duration: 5000
                    })
                    router.push({ name: 'cash-registers' })
                }
            } catch (error) {
                console.error('Erreur lors du chargement de la caisse active:', error)
            }
        }

        const searchProducts = () => {
            if (searchTimeout.value) {
                clearTimeout(searchTimeout.value)
            }
            searchTimeout.value = setTimeout(() => {
                loadProducts()
            }, 300)
        }

        const clearSearch = () => {
            searchQuery.value = ''
            loadProducts()
            nextTick(() => {
                searchInput.value?.focus()
            })
        }

        const navigateProducts = (direction) => {
            if (filteredProducts.value.length === 0) return

            if (direction === 'down') {
                highlightedProductIndex.value = (highlightedProductIndex.value + 1) % filteredProducts.value.length
            } else if (direction === 'up') {
                highlightedProductIndex.value = highlightedProductIndex.value <= 0
                    ? filteredProducts.value.length - 1
                    : highlightedProductIndex.value - 1
            }

            const highlightedRow = document.querySelector('.product-row.highlighted')
            if (highlightedRow) {
                highlightedRow.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
            }
        }

        const selectHighlightedProduct = () => {
            if (highlightedProductIndex.value >= 0 && highlightedProductIndex.value < filteredProducts.value.length) {
                addToCart(filteredProducts.value[highlightedProductIndex.value])
            }
        }

        const filterByCategory = () => {
            highlightedProductIndex.value = 0
        }

        const addToCart = (product) => {
            // Check if product is disabled (zero stock and settings don't allow it)
            if (isProductDisabled(product)) {
                toastStore.addToast({
                    type: 'warning',
                    message: 'Ce produit est en rupture de stock',
                    duration: 3000
                })
                return
            }

            const existingItemIndex = cart.value.findIndex(item => item.id === product.id)

            if (existingItemIndex !== -1) {
                cart.value[existingItemIndex].quantity += 1
                updateItemTotal(existingItemIndex)
            } else {
                const cartItem = {
                    id: product.id,
                    name: product.name,
                    price_excl_tax: product.price_excl_tax,
                    price_incl_tax: product.price_incl_tax,
                    quantity: 1,
                    tax_rate: product.tax_rate,
                    total: product.price_incl_tax
                }
                cart.value.push(cartItem)
            }

            nextTick(() => {
                searchInput.value?.focus()
            })
        }

        const updateItemTotal = (index) => {
            if (index >= 0 && index < cart.value.length) {
                const item = cart.value[index]
                if (item.quantity < 1) {
                    item.quantity = 1
                }
                item.total = item.price_incl_tax * item.quantity
            }
        }

        const onUnitPriceInput = (index) => {
            if (index < 0 || index >= cart.value.length) return
            const item = cart.value[index]
            const taxRate = Number(item.tax_rate) || 0
            if (!Number.isFinite(item.price_incl_tax) || item.price_incl_tax < 0) {
                item.price_incl_tax = 0
            }
            const divisor = 1 + (taxRate / 100)
            item.price_excl_tax = Math.round((item.price_incl_tax / divisor) * 100) / 100
            updateItemTotal(index)
        }

        const incrementQuantity = (index) => {
            if (index >= 0 && index < cart.value.length) {
                cart.value[index].quantity += 1
                updateItemTotal(index)
            }
        }

        const decrementQuantity = (index) => {
            if (index >= 0 && index < cart.value.length && cart.value[index].quantity > 1) {
                cart.value[index].quantity -= 1
                updateItemTotal(index)
            }
        }

        const removeFromCart = (index) => {
            if (index >= 0 && index < cart.value.length) {
                cart.value.splice(index, 1)
                toastStore.addToast({
                    type: 'info',
                    message: 'Article retiré du panier',
                    duration: 2000
                })
            }
        }

        const clearCart = () => {
            if (confirm('Êtes-vous sûr de vouloir vider le panier ?')) {
                cart.value = []
                discount.value = 0
                selectedPaymentMethod.value = 'cash'
                toastStore.addToast({
                    type: 'info',
                    message: 'Panier vidé',
                    duration: 2000
                })
            }
        }

        const calculateTotal = () => {
            if (discount.value > subtotal.value) {
                discount.value = subtotal.value
            }
        }

        const focusPayment = () => {
            if (paymentSection.value) {
                paymentSection.value.scrollIntoView({ behavior: 'smooth', block: 'nearest' })

                setTimeout(() => {
                    const firstPaymentOption = paymentSection.value.querySelector('.payment-option')
                    if (firstPaymentOption) {
                        firstPaymentOption.focus()
                    }
                }, 300)
            }
        }

        const openPaymentModal = () => {
            if (cart.value.length === 0) return
            showPaymentModal.value = true
        }

        const closePaymentModal = () => {
            showPaymentModal.value = false
        }

        const confirmPayment = async () => {
            if (!selectedPaymentMethod.value) return
            showPaymentModal.value = false
            await validateSale()
        }

        const validateSale = async () => {
            if (isProcessing.value || cart.value.length === 0) return

            if (!selectedPaymentMethod.value) {
                toastStore.addToast({
                    type: 'warning',
                    message: 'Veuillez sélectionner un mode de paiement',
                    duration: 3000
                })
                openPaymentModal()
                return
            }

            isProcessing.value = true

            try {
                const items = cart.value.map(item => ({
                    product_id: item.id,
                    quantity: item.quantity,
                    price_excl_tax: item.price_excl_tax
                }))

                const saleData = {
                    items,
                    discount: discount.value,
                    is_deferred: false,
                    cash_register_id: activeCashRegister.value?.id,
                    payment: {
                        payment_type: selectedPaymentMethod.value,
                        amount: total.value
                    }
                }

                const response = await salesService.createSale(saleData)
                completedSale.value = response.data.sale || {}
                showSuccessModal.value = true

                toastStore.addToast({
                    type: 'success',
                    message: 'Vente créée avec succès',
                    duration: 4000
                })

                await printTicket(response.data.sale)
            } catch (error) {
                console.error('Erreur lors de la création de la vente:', error);
                const errorResponse = error.response.data;
                if (errorResponse.message) {
                    toastStore.addToast({
                        type: 'error',
                        message: errorResponse.message,
                        duration: 4000
                    })
                } else {
                    toastStore.addToast({
                        type: 'error',
                        message: 'Erreur lors de la création de la vente',
                        duration: 4000
                    })
                }
            } finally {
                isProcessing.value = false
            }
        }

        const printTicket = async (sale) => {
            try {
                await thermalPrinterService.printTicket(sale, {
                    connectionType: 'usb',
                    shopInfo: JSON.parse(localStorage.getItem('company'))
                })
            } catch (error) {
                toastStore.addToast({
                    type: 'error',
                    message: error.message || 'Erreur lors de l\'impression du ticket',
                    duration: 4000
                })
            }
        }

        const printReceipt = (saleId) => {
            // Ouvrir le modal pour saisir les informations client
            invoiceSaleId.value = saleId
            invoiceClientName.value = ''
            invoiceClientPhone.value = ''
            invoiceErrors.value = { name: '', phone: '' }
            showInvoiceModal.value = true
        }

        const closeInvoiceModal = () => {
            showInvoiceModal.value = false
            invoiceClientName.value = ''
            invoiceClientPhone.value = ''
            invoiceErrors.value = { name: '', phone: '' }
            invoiceSaleId.value = null
        }

        const validateInvoiceForm = () => {
            invoiceErrors.value = { name: '', phone: '' }
            let isValid = true

            if (!invoiceClientName.value.trim()) {
                invoiceErrors.value.name = 'Le nom du client est obligatoire'
                isValid = false
            }

            if (!invoiceClientPhone.value.trim()) {
                invoiceErrors.value.phone = 'Le téléphone est obligatoire'
                isValid = false
            }

            return isValid
        }

        const confirmDownloadInvoice = async () => {
            if (!validateInvoiceForm()) return

            isDownloadingInvoice.value = true
            try {
                await salesService.downloadInvoice(invoiceSaleId.value, false, {
                    name: invoiceClientName.value.trim(),
                    phone: invoiceClientPhone.value.trim()
                })
                toastStore.addToast({
                    type: 'success',
                    message: 'Téléchargement de la facture en cours...',
                    duration: 3000
                })
                closeInvoiceModal()
            } catch (err) {
                toastStore.addToast({
                    type: 'error',
                    message: 'Erreur lors du téléchargement de la facture',
                    duration: 4000
                })
            } finally {
                isDownloadingInvoice.value = false
            }
        }

        const newSale = () => {
            cart.value = []
            discount.value = 0
            selectedPaymentMethod.value = 'cash'
            showSuccessModal.value = false
            completedSale.value = {}
            searchQuery.value = ''
            selectedCategory.value = ''
            loadProducts()
            nextTick(() => {
                searchInput.value?.focus()
            })
        }

        const closeSuccessModal = () => {
            showSuccessModal.value = false
        }

        const goBack = () => {
            router.back()
        }

        const formatCurrency = (amount) => {
            return new Intl.NumberFormat('fr-FR', {
                style: 'currency',
                currency: 'XOF',
                minimumFractionDigits: 0
            }).format(amount || 0)
        }

        const getStockClass = (quantity) => {
            if (quantity === 0 || quantity === null || quantity === undefined) return 'out-of-stock'
            if (quantity <= 5) return 'low-stock'
            return 'in-stock'
        }

        const getPaymentMethodLabel = (value) => {
            const method = paymentMethods.find(m => m.value === value)
            return method ? method.label : value
        }

        return {
            loading,
            products,
            categories,
            searchQuery,
            selectedCategory,
            cart,
            discount,
            isProcessing,
            showSuccessModal,
            showPaymentModal,
            completedSale,
            selectedPaymentMethod,
            paymentMethods,
            highlightedProductIndex,
            searchInput,
            paymentSection,
            filteredProducts,
            subtotal,
            total,
            searchProducts,
            clearSearch,
            navigateProducts,
            selectHighlightedProduct,
            filterByCategory,
            addToCart,
            updateItemTotal,
            onUnitPriceInput,
            incrementQuantity,
            decrementQuantity,
            removeFromCart,
            clearCart,
            calculateTotal,
            focusPayment,
            openPaymentModal,
            closePaymentModal,
            confirmPayment,
            validateSale,
            printReceipt,
            newSale,
            closeSuccessModal,
            goBack,
            formatCurrency,
            getStockClass,
            getPaymentMethodLabel,
            printTicket,
            isProductDisabled,
            // Invoice modal
            showInvoiceModal,
            invoiceClientName,
            invoiceClientPhone,
            invoiceErrors,
            isDownloadingInvoice,
            closeInvoiceModal,
            confirmDownloadInvoice
        }
    }
})
</script>

<style scoped>
.sale-simple-view {
    padding: 0 0 40px 0;
}

.page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
}

.sale-container {
    display: flex;
    gap: 20px;
    height: calc(100vh - 150px);
    min-height: 600px;
}

.products-column {
    flex: 0.60;
    display: flex;
    flex-direction: column;
    background-color: white;
    border-radius: 10px;
    border: 1.5px solid #e2e8f0;
    overflow: hidden;
    position: relative;
}

.cart-column {
    flex: 0.40;
    display: flex;
    flex-direction: column;
    background-color: white;
    border-radius: 10px;
    border: 1.5px solid #e2e8f0;
    overflow: hidden;
}

.search-container {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 20px;
    border-bottom: 1.5px solid #f1f5f9;
    background-color: #fafbfc;
}

.search-input-wrapper {
    position: relative;
    flex: 1;
}

.search-input-wrapper svg:first-child,
.search-input-wrapper > i:first-child {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
    z-index: 1;
}

.search-input {
    width: 100%;
    padding: 12px 44px 12px 44px;
    border: 1.5px solid #e2e8f0;
    border-radius: 8px;
    font-size: 14px;
    background-color: #ffffff;
    transition: all 0.2s;
}

.search-input:focus {
    outline: none;
    border-color: #0A5C6B;
    background-color: #f8fafc;
    box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.1);
}

.clear-search-btn {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: #94a3b8;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: all 0.2s;
}

.clear-search-btn:hover {
    color: #64748b;
    background-color: #f1f5f9;
}

.categories-filter select {
    padding: 12px 36px 12px 14px;
    border: 1.5px solid #e2e8f0;
    border-radius: 8px;
    background-color: #ffffff;
    font-size: 14px;
    min-width: 200px;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
    background-position: right 12px center;
    background-repeat: no-repeat;
    cursor: pointer;
    transition: all 0.2s;
}

.categories-filter select:focus {
    outline: none;
    border-color: #0A5C6B;
    box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.1);
}

.products-list {
    flex: 1;
    overflow-y: auto;
    padding: 0;
}

.loading-container,
.empty-results {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    text-align: center;
    height: 100%;
}

.loading-container svg,
.empty-results svg {
    color: #cbd5e1;
    margin-bottom: 16px;
}

.loading-container p,
.empty-results p {
    color: #94a3b8;
    margin: 0 0 8px 0;
    font-size: 14px;
}

.clear-search-link {
    background: none;
    border: none;
    color: #0A5C6B;
    text-decoration: underline;
    cursor: pointer;
    font-size: 14px;
    padding: 8px 16px;
    margin-top: 8px;
    border-radius: 6px;
    transition: all 0.2s;
}

.clear-search-link:hover {
    background-color: #f8fafc;
}

.spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #f1f5f9;
    border-top: 3px solid #0A5C6B;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 16px;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

.spinner-small {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.5);
    border-top: 2px solid white;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    display: inline-block;
    margin-right: 8px;
}

.products-table-container {
    width: 100%;
    overflow-x: auto;
}

.products-table {
    width: 100%;
    border-collapse: collapse;
    background: white;
}

.products-table thead {
    background: #f8fafc;
    position: sticky;
    top: 0;
    z-index: 10;
}

.products-table th {
    padding: 12px 16px;
    text-align: left;
    font-size: 12px;
    font-weight: 600;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: 2px solid #e2e8f0;
}

.products-table tbody tr {
    border-bottom: 1px solid #f1f5f9;
    cursor: pointer;
    transition: all 0.15s;
}

.products-table tbody tr:hover,
.products-table tbody tr.highlighted {
    background: #f0f9fa;
    border-left: 3px solid #0A5C6B;
}

.products-table td {
    padding: 10px 16px;
    font-size: 14px;
    color: #1e293b;
}

.col-image {
    width: 60px;
}

.col-name {
    min-width: 200px;
}

.col-category {
    width: 140px;
}

.col-stock {
    width: 80px;
    text-align: center;
}

.col-price {
    width: 120px;
}

.col-action {
    width: 60px;
    text-align: center;
}

.product-image-small {
    width: 40px;
    height: 40px;
    background-color: #f8fafc;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    border: 1px solid #e2e8f0;
}

.product-image-small img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.placeholder-image-small {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    color: #cbd5e1;
}

.product-name-text {
    font-weight: 600;
    color: #1e293b;
    display: block;
    margin-bottom: 2px;
}

.product-reference {
    font-size: 12px;
    color: #94a3b8;
    display: block;
}

.category-badge {
    display: inline-block;
    padding: 4px 10px;
    background: #f1f5f9;
    color: #64748b;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
}

.stock-badge {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
}

.stock-badge.in-stock {
    background: #d1fae5;
    color: #065f46;
}

.stock-badge.low-stock {
    background: #fef3c7;
    color: #92400e;
}

.stock-badge.out-of-stock {
    background: #fee2e2;
    color: #991b1b;
}

.product-price-text {
    font-weight: 700;
    color: #0A5C6B;
    font-size: 15px;
}

.add-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    background: #0A5C6B;
    color: white;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s;
}

.add-btn:hover:not(:disabled) {
    background: #094854;
    transform: scale(1.05);
}

.add-btn:disabled {
    background: #cbd5e1;
    cursor: not-allowed;
    opacity: 0.6;
}

.add-btn svg {
    width: 16px;
    height: 16px;
}

/* Disabled product row */
.product-row.product-disabled {
    opacity: 0.5;
    background: #f8fafc;
    cursor: not-allowed;
}

.product-row.product-disabled:hover {
    background: #f8fafc;
    border-left: none;
}

.product-row.product-disabled .product-name-text {
    color: #94a3b8;
}

.product-row.product-disabled .product-price-text {
    color: #94a3b8;
}

.quick-actions-bar {
    position: sticky;
    bottom: 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    background: linear-gradient(to top, #ffffff 0%, #fafbfc 100%);
    border-top: 1.5px solid #e2e8f0;
    box-shadow: 0 -4px 6px -1px rgba(0, 0, 0, 0.05);
    z-index: 10;
}

.quick-info {
    display: flex;
    align-items: center;
    gap: 16px;
}

.items-count {
    font-size: 14px;
    color: #64748b;
    font-weight: 500;
}

.quick-total {
    font-size: 16px;
    color: #0f172a;
    font-weight: 700;
}

.quick-validate-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background: #0A5C6B;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.quick-validate-btn:hover {
    background: #094854;
    transform: translateY(-1px);
    box-shadow: 0 4px 6px -1px rgba(10, 92, 107, 0.3);
}

.cart-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    border-bottom: 1.5px solid #f1f5f9;
    background-color: #fafbfc;
}

.cart-header h2 {
    font-size: 18px;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
}

.clear-cart-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    background-color: #fef2f2;
    color: #dc2626;
    border: 1.5px solid #fecaca;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
}

.clear-cart-btn:hover {
    background-color: #fee2e2;
    border-color: #fca5a5;
}

.empty-cart {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    text-align: center;
    height: 300px;
}

.empty-cart-icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: #f1f5f9;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #cbd5e1;
    margin-bottom: 16px;
}

.empty-cart p {
    color: #94a3b8;
    margin: 0;
    font-size: 14px;
}

.empty-cart-hint {
    font-size: 12px !important;
    color: #cbd5e1 !important;
    margin-top: 8px !important;
}

.cart-items {
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    min-height: 200px;
}

.cart-item {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 14px;
    border-bottom: 1.5px solid #f8fafc;
    transition: background-color 0.2s;
    border-radius: 8px;
    margin-bottom: 8px;
}

.cart-item:hover {
    background-color: #fafbfc;
}

.cart-item:last-child {
    border-bottom: none;
}

.item-details {
    flex: 1;
    min-width: 0;
}

.item-name {
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
    margin-bottom: 4px;
}

.item-price {
    font-size: 13px;
    color: #64748b;
}

.item-price .price-input {
    width: 90px;
    text-align: right;
    padding: 6px 8px;
    border: 1.5px solid #e2e8f0;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    margin-right: 6px;
}

.item-price .price-input:focus {
    outline: none;
    border-color: #0A5C6B;
    box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.1);
}

.item-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.item-quantity {
    display: flex;
    align-items: center;
    gap: 8px;
}

.item-quantity input {
    width: 50px;
    text-align: center;
    padding: 6px;
    border: 1.5px solid #e2e8f0;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
}

.item-quantity input:focus {
    outline: none;
    border-color: #0A5C6B;
    box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.1);
}

.quantity-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    background-color: #f8fafc;
    border: 1.5px solid #e2e8f0;
    border-radius: 6px;
    color: #64748b;
    cursor: pointer;
    transition: all 0.2s;
}

.quantity-btn:hover {
    background-color: #e0f7fa;
    border-color: #0A5C6B;
    color: #0A5C6B;
}

.item-total {
    font-size: 15px;
    font-weight: 700;
    color: #0A5C6B;
    min-width: 90px;
    text-align: right;
}

.remove-item-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    background-color: #fef2f2;
    border: 1.5px solid #fecaca;
    border-radius: 6px;
    color: #dc2626;
    cursor: pointer;
    transition: all 0.2s;
}

.remove-item-btn:hover {
    background-color: #fee2e2;
    border-color: #fca5a5;
}

.cart-summary {
    padding: 20px;
    border-top: 1.5px solid #f1f5f9;
    background-color: #fafbfc;
    min-height: 150px;
    overflow-y: auto;
}

.summary-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 14px;
    font-size: 14px;
    color: #64748b;
}

.summary-row:last-child {
    margin-bottom: 0;
}

.subtotal-row {
    padding-bottom: 10px;
}

.subtotal-amount {
    font-weight: 600;
    color: #475569;
}

.total-row {
    font-size: 16px;
    font-weight: 700;
    color: #0f172a;
    padding-top: 14px;
    border-top: 2px solid #e2e8f0;
    margin-bottom: 20px;
}

.total-amount {
    color: #0A5C6B;
    font-size: 20px;
}

.payment-modal-title {
    font-size: 18px;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 12px 0;
}

.payment-modal-total {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 0 16px 0;
    border-bottom: 1.5px solid #e2e8f0;
    margin-bottom: 14px;
}

.discount-row {
    margin-bottom: 16px;
    padding-bottom: 14px;
}

.discount-label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: #475569;
}

.discount-input {
    position: relative;
    width: 140px;
}

.discount-input input {
    width: 100%;
    padding: 10px 50px 10px 12px;
    border: 1.5px solid #e2e8f0;
    border-radius: 6px;
    font-size: 14px;
    transition: all 0.2s;
}

.discount-input input:focus {
    outline: none;
    border-color: #0A5C6B;
    box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.1);
}

.currency-symbol {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
    font-size: 13px;
    font-weight: 500;
}

.payment-subtitle {
    font-size: 13px;
    font-weight: 600;
    color: #475569;
    margin: 0 0 12px 0;
}

.payment-modal-container {
    max-width: 560px;
}

.payment-options {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
}

.payment-option {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 12px 8px;
    border: 2px solid #e2e8f0;
    border-radius: 8px;
    background-color: #ffffff;
    cursor: pointer;
    transition: all 0.2s;
}

.payment-option:hover {
    border-color: #0A5C6B;
    background-color: #f8fafc;
    transform: translateY(-2px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.payment-option:focus {
    outline: none;
    border-color: #0A5C6B;
    box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.1);
}

.payment-option.active {
    border-color: #0A5C6B;
    background-color: #e0f7fa;
    box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.1);
}

.payment-option.active .payment-icon {
    color: #0A5C6B;
}

.payment-check {
    position: absolute;
    top: 6px;
    right: 6px;
    width: 20px;
    height: 20px;
    background: #0A5C6B;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
}

.payment-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #64748b;
    transition: color 0.2s;
}

.payment-name {
    font-size: 12px;
    font-weight: 500;
    color: #475569;
    text-align: center;
}

.flex {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
}

.validate-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    text-wrap: nowrap;
    width: max-content;
    padding: 10px 20px;
    background-color: #0A5C6B;
    color: white;
    border: 1.5px solid #0A5C6B;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
    font-size: 14px;
    font-weight: 600;
}

.validate-btn:hover:not(:disabled) {
    background-color: #094854;
    transform: translateY(-1px);
    box-shadow: 0 4px 6px -1px rgba(10, 92, 107, 0.3);
}

.validate-btn:active:not(:disabled) {
    transform: translateY(0);
}

.validate-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.validate-cart-btn {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 14px 20px;
    background-color: #0A5C6B;
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
    font-size: 15px;
    font-weight: 600;
}

.validate-cart-btn:hover:not(:disabled) {
    background-color: #094854;
    transform: translateY(-1px);
    box-shadow: 0 6px 10px -2px rgba(10, 92, 107, 0.3);
}

.validate-cart-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(15, 23, 42, 0.6);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 20px;
}

.modal-container {
    background-color: white;
    border-radius: 16px;
    width: 100%;
    max-width: 520px;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    animation: modal-appear 0.25s ease-out;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

.modal-container.success-modal {
    max-width: 60%;
}

@keyframes modal-appear {
    from {
        opacity: 0;
        transform: scale(0.95) translateY(10px);
    }

    to {
        opacity: 1;
        transform: scale(1) translateY(0);
    }
}

.modal-body {
    padding: 32px 24px;
    overflow-y: auto;
}

.modal-body.text-center {
    text-align: center;
}

.modal-footer {
    padding: 20px 24px;
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    border-top: 1px solid #f1f5f9;
    background-color: #f8fafc;
    border-radius: 0 0 16px 16px;
}

.success-modal-footer {
    flex-direction: column;
    justify-content: center;
}

.success-icon {
    display: flex;
    justify-content: center;
    margin-bottom: 20px;
    color: #10b981;
}

.success-modal h2 {
    font-size: 22px;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 10px 0;
    text-align: center;
}

.success-modal p {
    font-size: 15px;
    color: #64748b;
    margin: 0 0 20px 0;
    text-align: center;
}

.invoice-number {
    font-family: monospace;
    font-weight: 700;
    color: #0A5C6B;
    background: #e0f7fa;
    padding: 2px 8px;
    border-radius: 4px;
}

.success-details {
    background-color: #f8fafc;
    padding: 16px;
    border-radius: 8px;
    margin-bottom: 16px;
}

.detail-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    font-size: 14px;
    color: #64748b;
}

.detail-row:not(:last-child) {
    border-bottom: 1px solid #e2e8f0;
}

.detail-value {
    font-weight: 600;
    color: #1e293b;
}

.success-total {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: linear-gradient(135deg, #e0f7fa 0%, #b2ebf2 100%);
    padding: 20px;
    border-radius: 8px;
    margin-top: 20px;
}

.success-total span:first-child {
    font-size: 14px;
    color: #0A5C6B;
    font-weight: 600;
}

.success-amount {
    font-size: 24px;
    font-weight: 700;
    color: #0A5C6B;
}

.print-btn,
.new-sale-btn,
.back-to-sales-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px 18px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.print-btn {
    background-color: #f8fafc;
    color: #475569;
    border: 1.5px solid #e2e8f0;
}

.print-btn:hover {
    background-color: #f1f5f9;
    border-color: #cbd5e1;
    transform: translateY(-1px);
}

.new-sale-btn {
    background-color: #0A5C6B;
    color: white;
    border: none;
}

.new-sale-btn:hover {
    background-color: #094854;
    transform: translateY(-1px);
    box-shadow: 0 4px 6px -1px rgba(10, 92, 107, 0.3);
}

.back-to-sales-btn {
    background-color: #eff6ff;
    color: #0A5C6B;
    border: 1.5px solid #dbeafe;
}

.back-to-sales-btn:hover {
    background-color: #dbeafe;
    border-color: #bfdbfe;
    transform: translateY(-1px);
}

@media (max-width: 1024px) {
    .sale-container {
        flex-direction: column;
        height: auto;
    }

    .products-column,
    .cart-column {
        width: 100%;
        max-height: none;
    }

    .products-list {
        max-height: 500px;
    }

    .cart-items {
        max-height: 200px;
    }

    .payment-options {
        grid-template-columns: repeat(3, 1fr);
    }
}

@media (max-width: 768px) {
    .search-container {
        flex-direction: column;
        align-items: stretch;
    }

    .categories-filter select {
        width: 100%;
    }

    .payment-options {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 480px) {
    .modal-container {
        width: 95%;
        max-width: none;
    }

    .modal-footer {
        flex-direction: column;
    }

    .modal-footer button {
        width: 100%;
    }

    .page-heading {
        font-size: 22px;
    }

    .payment-options {
        grid-template-columns: 1fr;
    }

    .action-cards {
        grid-template-columns: repeat(3, 1fr);
        gap: 8px;
    }

    .action-card {
        padding: 16px 12px;
    }

    .action-card-icon {
        width: 48px;
        height: 48px;
    }

    .action-card-label {
        font-size: 11px;
    }
}

/* Action Cards */
.action-cards {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
}

.action-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 20px 16px;
    border-radius: 12px;
    background: white;
    border: 2px solid #e2e8f0;
    cursor: pointer;
    transition: all 0.2s ease;
}

.action-card:hover {
    border-color: #0A5C6B;
    transform: translateY(-2px);
    box-shadow: 0 8px 16px -4px rgba(10, 92, 107, 0.15);
}

.action-card:active {
    transform: translateY(0);
}

.action-card.primary {
    background: linear-gradient(135deg, #0A5C6B 0%, #0d7a8c 100%);
    border-color: #0A5C6B;
}

.action-card.primary:hover {
    background: linear-gradient(135deg, #094854 0%, #0A5C6B 100%);
    border-color: #094854;
    box-shadow: 0 8px 16px -4px rgba(10, 92, 107, 0.3);
}

.action-card-icon {
    width: 56px;
    height: 56px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.invoice-icon {
    background: #eff6ff;
    color: #3b82f6;
}

.ticket-icon {
    background: #fef3c7;
    color: #f59e0b;
}

.new-sale-icon {
    background: rgba(255, 255, 255, 0.2);
    color: white;
}

.action-card-label {
    font-size: 13px;
    font-weight: 600;
    color: #475569;
}

.action-card.primary .action-card-label {
    color: white;
}

.back-link {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 10px;
    background: transparent;
    border: none;
    color: #64748b;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: color 0.2s;
}

.back-link:hover {
    color: #0A5C6B;
}

/* Invoice Modal Styles */
.invoice-modal {
    width: 100%;
    max-width: 640px;
    background: white;
    border-radius: 16px;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    animation: modalSlideIn 0.3s ease;
}

.modal-description {
    color: #64748b;
    font-size: 14px;
    margin: 0 0 20px 0;
    line-height: 1.5;
}

.form-group {
    margin-bottom: 16px;
}

.form-group label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 6px;
}

.form-group .required {
    color: #ef4444;
}

.form-input {
    width: 100%;
    padding: 12px 14px;
    border: 1.5px solid #e2e8f0;
    border-radius: 10px;
    font-size: 14px;
    color: #1e293b;
    background: #f8fafc;
    transition: all 0.2s;
    box-sizing: border-box;
}

.form-input:focus {
    outline: none;
    border-color: #0A5C6B;
    background: white;
    box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.1);
}

.form-input::placeholder {
    color: #94a3b8;
}

.form-input.input-error {
    border-color: #ef4444;
    background: #fef2f2;
}

.form-input.input-error:focus {
    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

.error-message {
    display: block;
    color: #ef4444;
    font-size: 12px;
    margin-top: 4px;
}

.invoice-modal-footer {
    display: flex;
    flex-direction: row;
    gap: 12px;
    padding-top: 20px;
    border-top: 1px solid #e2e8f0;
    margin-top: 8px;
}

.btn-cancel {
    padding: 10px 20px;
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    color: #475569;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-cancel:hover {
    background: #e2e8f0;
}

.btn-confirm {
    padding: 10px 24px;
    background: linear-gradient(135deg, #0A5C6B 0%, #0d7a8c 100%);
    border: none;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    color: white;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    gap: 8px;
}

.btn-confirm:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(10, 92, 107, 0.3);
}

.btn-confirm:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}
</style>